import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Props = {
  fileName: string
  /** Null while the server is still reading. */
  steps: string[] | null
  onContinue: () => void
}

const STEP_DELAY_MS = 420

/**
 * Soly reads out loud what it found, one fact at a time. The file is read in milliseconds; the
 * cadence is for the person, who needs to see the work to trust it.
 */
export function WinnerReading({ fileName, steps, onContinue }: Props) {
  const [shown, setShown] = useState(0)
  const total = steps?.length ?? 0
  const finished = steps !== null && shown >= total

  useEffect(() => {
    if (steps === null || shown >= steps.length) return
    const timer = window.setTimeout(() => setShown((n) => n + 1), STEP_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [steps, shown])

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-6 py-10">
      <div className="space-y-1">
        <h3 className="text-xl font-semibold tracking-tight">Soly lit votre dossier</h3>
        <p className="truncate text-sm text-muted-foreground">{fileName}</p>
      </div>

      <ul className="space-y-3" data-testid="winner-import-reading-steps">
        {(steps ?? []).slice(0, shown).map((step, index) => (
          <li key={step} className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-1 duration-300">
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                index === total - 1 ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white'
              )}
            >
              <Check className="h-3 w-3" />
            </span>
            <span className={cn('text-sm', index === total - 1 && 'font-medium text-emerald-700')}>{step}</span>
          </li>
        ))}
        {!finished && (
          <li className="flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Lecture en cours…
          </li>
        )}
      </ul>

      <div className={cn('transition-opacity duration-300', finished ? 'opacity-100' : 'pointer-events-none opacity-0')}>
        <Button size="lg" onClick={onContinue} data-testid="winner-import-reading-continue-btn">
          Vérifier le dossier
        </Button>
      </div>
    </div>
  )
}
